import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@fb-tool-front/environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, publishReplay, refCount, take, tap } from 'rxjs/operators';
import param from 'jquery-param';
import { Ad, Adset, Campaign } from '@fb-tool-front/shared/interfaces/ad-account.type';
import { AuthService } from './auth.service';
import { Client } from '@fb-tool-front/shared/interfaces/auth.type';
import {
  AdAccount,
  AdAccountBreadcrumbsRequestParams,
  AdAccountBreadcrumbsResponse,
  AdAccountInfo,
  AdAccountRequestParams,
  AppointClientReqBody
} from '@fb-tool-front/statistics/adaccounts/adaccounts.models';

@Injectable({
  providedIn: 'root'
})
export class AdAccountsService {
  private FACEBOOK_URL = `${environment.host}/api/facebook`;

  private cache: { [key: string]: Observable<AdAccountInfo> } = {};
  private adAccounts: AdAccount[] = null;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.redirectToVendor$.subscribe(() => (this.adAccounts = null));
  }

  private fillInClientFields([adAccounts, allClients]: [AdAccount[], Client[]]) {
    return adAccounts?.map(adAccount => {
      adAccount.isAppointToClient = adAccount?.client === this.authService.getCurrentClient().id;
      adAccount.isAnotherClient =
        adAccount?.client && adAccount?.client !== this.authService.getCurrentClient().id;
      if (adAccount.isAnotherClient) {
        adAccount.anotherClient = allClients.find(client => client.id === adAccount.client)?.name;
      }

      return adAccount;
    });
  }

  public get(params?: AdAccountRequestParams, saveInCache = true) {
    if (this.adAccounts) {
      return of(this.adAccounts);
    } else {
      return forkJoin([
        this.http
          .get<AdAccount[]>(`${this.FACEBOOK_URL}/adaccounts`, {
            params: new HttpParams({ fromString: param(params) })
          })
          .pipe(map((response: AdAccount[]) => (response ? response : []))),
        this.authService.allClientsList$.pipe(take(1))
      ]).pipe(
        map((res: [AdAccount[], Client[]]) => this.fillInClientFields(res)),
        tap(res => (saveInCache ? (this.adAccounts = res) : null))
      );
    }
  }

  public appointClients(body: AppointClientReqBody) {
    return forkJoin([
      this.http.post<AdAccount[] | null>(`${this.FACEBOOK_URL}/adaccounts/client`, body),
      this.authService.allClientsList$.pipe(take(1))
    ]).pipe(
      map((res: [AdAccount[] | null, Client[]]) => this.fillInClientFields(res)),
      tap(res => (body.no_return ? null : (this.adAccounts = res)))
    );
  }

  public getInfo(id: string): Observable<AdAccountInfo> {
    return this.http.get<AdAccountInfo>(`${this.FACEBOOK_URL}/adaccounts/${id}/info`);
  }

  public getCachedInfo(id: string): Observable<AdAccountInfo> {
    if (!this.cache[id]) {
      this.cache[id] = this.getInfo(id).pipe(publishReplay(1), refCount());
    }

    return this.cache[id];
  }

  public getBreadcrumbs(
    params: AdAccountBreadcrumbsRequestParams
  ): Observable<AdAccount | Campaign | Adset | Ad> {
    return this.http
      .get<AdAccountBreadcrumbsResponse>(`${environment.host}/ecom-video-tool/breadcrumbs`, {
        // TODO: need new api
        params: new HttpParams({ fromString: param(params) })
      })
      .pipe(map((response: AdAccountBreadcrumbsResponse) => response[params.type]));
  }

  public getReport(body) {
    return this.http.post(`${environment.host}/facebook/report/dashboard`, body); // TODO: need new api
  }
}
